import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { defaultEQM3010_R01Dto, EQM3010_R01Dto } from './Dto_EQM3010_R01';

/** 검교정 계획 조회 검색 조건 */
export interface EQM3010_R01Filter {
	compCd: string;
	year: number;
	equipmentCd: string;
	equipmentNm: string;
}

/** 검색 조건 초기값 (연도: 올해) */
export const defaultEQM3010_R01Filter: EQM3010_R01Filter = {
	compCd: '',
	year: new Date().getFullYear(),
	equipmentCd: '',
	equipmentNm: '',
};

export interface Store_EQM3010_R01 {
	EQM3010_R01Filter: EQM3010_R01Filter;
	EQM3010_R01PastFilter: EQM3010_R01Filter;
	setEQM3010_R01Filter: (filter: Partial<EQM3010_R01Filter>) => void;
	setEQM3010_R01PastFilter: (filter: Partial<EQM3010_R01Filter>) => void;

	EQM3010_R01List: EQM3010_R01Dto[];
	setEQM3010_R01List: (list: EQM3010_R01Dto[]) => void;
	updateEQM3010_R01List: (index: number, data: EQM3010_R01Dto) => void;

	deletedEQM3010_R01List: string[];
	addDeletedEQM3010_R01List: (idList: string[]) => void;
	removeDeletedEQM3010_R01List: (idList?: string[]) => void;

	selectedEQM3010_R01List: string[];
	setSelectedEQM3010_R01List: (idList: string[]) => void;
	removeSelectedEQM3010_R01List: (idList?: string[]) => void;
	initSelectedEQM3010_R01List: () => void;
	allSelectedEQM3010_R01List: () => void;

	originalEQM3010_R01List: EQM3010_R01Dto[];
	setOriginalEQM3010_R01List: (list: EQM3010_R01Dto[]) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	allCount: number;
	setAllCount: (allCount: number) => void;

	initData: () => void;
}

export const useEQM3010_R01Store = create<Store_EQM3010_R01>((set) => ({
	EQM3010_R01Filter: defaultEQM3010_R01Filter,
	EQM3010_R01PastFilter: defaultEQM3010_R01Filter,

	setEQM3010_R01Filter: (update) => set((s) => ({
		EQM3010_R01Filter: { ...s.EQM3010_R01Filter, ...update },
	})),
	setEQM3010_R01PastFilter: (update) => set((s) => ({
		EQM3010_R01PastFilter: { ...s.EQM3010_R01PastFilter, ...update },
	})),

	EQM3010_R01List: [],

	setEQM3010_R01List: (list) => set((s) => {
		const newList: EQM3010_R01Dto[] = [];
		let idx = 1;
		list.forEach((item) => {
			newList.push({
				...defaultEQM3010_R01Dto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : idx,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : idx,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
			});
			idx++;
		});
		return { ...s, EQM3010_R01List: newList };
	}),

	updateEQM3010_R01List: (valueIndex, data) => set((s) => ({
		...s,
		EQM3010_R01List: s.EQM3010_R01List.map((i) => i.valueIndex === valueIndex ? data : i),
	})),

	deletedEQM3010_R01List: [],

	addDeletedEQM3010_R01List: (idList) => set((s) => {
		const deleted = [...s.deletedEQM3010_R01List];
		for (const v of idList) {
			const inserted = s.EQM3010_R01List.find((i) => i.valueIndex === Number(v) && i.inserted);
			const modified = s.EQM3010_R01List.find((i) => i.valueIndex === Number(v) && i.isModified);
			if (!inserted && !modified) deleted.push(v);
		}
		return { ...s, deletedEQM3010_R01List: deleted };
	}),
	removeDeletedEQM3010_R01List: (idList) => set((s) => ({
		...s,
		deletedEQM3010_R01List: s.deletedEQM3010_R01List.filter(
			(v) => (idList ? !idList.includes(v) : false)),
	})),

	selectedEQM3010_R01List: [],
	setSelectedEQM3010_R01List: (idList) => set({ selectedEQM3010_R01List: idList }),
	removeSelectedEQM3010_R01List: (idList) => set((s) => ({
		...s,
		selectedEQM3010_R01List: s.selectedEQM3010_R01List.filter(
			(v) => (idList ? !idList.includes(v) : false)),
	})),
	initSelectedEQM3010_R01List: () => set({ selectedEQM3010_R01List: [] }),
	allSelectedEQM3010_R01List: () => set((s) => ({
		...s,
		selectedEQM3010_R01List: s.EQM3010_R01List.map((i) => i.valueIndex.toString()),
	})),

	originalEQM3010_R01List: [],

	setOriginalEQM3010_R01List: (list) => set((s) => {
		const newList: EQM3010_R01Dto[] = [];
		let idx = 1;
		list.forEach((item) => {
			newList.push({
				...defaultEQM3010_R01Dto,
				...item,
				valueIndex: idx,
				orderIndex: idx,
				orderIndexSeq: idx,
			});
			idx++;
		});
		return { ...s, originalEQM3010_R01List: newList };
	}),

	columnState: null,
	setColumnState: (columnState) => set({ columnState }),
	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),
	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),

	initData: () => set((s) => ({
		...s,
		EQM3010_R01Filter: defaultEQM3010_R01Filter,
		EQM3010_R01PastFilter: defaultEQM3010_R01Filter,
		EQM3010_R01List: [],
		selectedEQM3010_R01List: [],
		deletedEQM3010_R01List: [],
		allCount: 0,
		scrollTop: null,
	})),
}));
